/**
 * Calidad de código: las filas por página se calculan con la altura de la
 * ventana (Norman no quiere pantallas que rueden): las que caben entre la
 * cabecera de la tabla y el final de la ventana, descontando el pie de la
 * tarjeta y el aire de la página. En la lista de proyectos del tablero, los
 * renglones que caben en cada página y lo que crece cada uno (mideLista,
 * porPaginaLista y altoLista).
 */

/** Una página de una lista, 1-based. */
export interface Pagina {
  pagina: number;
  porPagina: number;
}

/** Lo que ocupa el pie de la tarjeta (table-page__footer) cuando todavía no está montado. */
const ALTO_PIE_OMISION = 58;
/** Hasta cuánto aire extra se reparte en cada fila para que la tarjeta llegue al final de la ventana. */
const EXTRA_MAXIMO = 14;

/** Lo que cabe: las filas por página y el aire que sobra, ya repartido por fila (píxeles). */
export interface Ajuste {
  porPagina: number;
  /** Lo que se suma al relleno de cada fila (0 cuando la lista no llena la página). */
  extraFila: number;
}

/**
 * Cuántas filas caben debajo de la cabecera de `tabla` hasta el final de la
 * ventana, entre `minimo` y `maximo`. Se suman los altos REALES de las filas
 * pintadas, en orden (unas traen dos renglones y otras uno, así que el
 * promedio engaña y hacía brincar la cuenta entre dos valores): si alguna ya
 * no cabe, la página se queda con las que sí; si caben todas, el resto se
 * reparte con el alto de la fila más alta (o `altoFila` si no hay ninguna).
 * Lo que sobra se reparte entre las filas (`extraFila`) para no dejar un
 * hueco, con tope. Quien lo llama vuelve a medir una vez tras repintar.
 */
export function ajustePaginado(tabla: HTMLElement | null | undefined, margen: number, minimo: number, maximo: number, altoFila: number, total: number): Ajuste {
  if (!tabla) {
    return { porPagina: minimo, extraFila: 0 };
  }
  const cabecera = tabla.querySelector("thead");
  const extraActual = Number.parseFloat(tabla.style.getPropertyValue("--cc-fila-extra")) || 0;
  const alturas = acotadas(Array.from(tabla.querySelectorAll("tbody tr"), (f) => Math.max(24, f.getBoundingClientRect().height - extraActual)));
  const arriba = (cabecera ?? tabla).getBoundingClientRect().bottom;
  const pie = tabla.closest(".table-page__card")?.querySelector(".table-page__footer")?.getBoundingClientRect().height ?? ALTO_PIE_OMISION;
  const disponible = Math.max(0, window.innerHeight - arriba - pie - margen);
  const caben = filasQueCaben(alturas, disponible);
  let porPagina: number;
  if (caben < alturas.length) {
    porPagina = caben;
  } else {
    const ocupado = alturas.reduce((s, a) => s + a, 0);
    const referencia = alturas.length > 0 ? Math.max(...alturas) : altoFila;
    porPagina = alturas.length + Math.floor((disponible - ocupado) / referencia);
  }
  porPagina = Math.min(maximo, Math.max(minimo, porPagina));
  const visibles = Math.min(porPagina, total);
  const ocupadoVisible = alturas.slice(0, visibles).reduce((s, a) => s + a, 0);
  const sobra = visibles > 0 && alturas.length >= visibles ? Math.max(0, disponible - ocupadoVisible) : 0;
  const extraFila = visibles >= porPagina ? Math.min(EXTRA_MAXIMO, Math.floor(sobra / visibles)) : 0;
  return { porPagina, extraFila };
}

/**
 * Ninguna fila cuenta con más de 2.5 veces la mediana: en el primer cuadro
 * de una pestaña recién abierta la última fila llega a medir 150 o 280 px
 * (los chips se apilan antes de que la tabla tome su ancho) y se llevaba la
 * cuenta a menos filas de las que caben.
 */
function acotadas(alturas: number[]): number[] {
  if (alturas.length < 3) {
    return alturas;
  }
  const ordenadas = [...alturas].sort((a, b) => a - b);
  const tope = ordenadas[Math.floor(ordenadas.length / 2)] * 2.5;
  return alturas.map((a) => Math.min(a, tope));
}

/** Cuántas de las filas, en orden, caben en `disponible` píxeles. */
function filasQueCaben(alturas: number[], disponible: number): number {
  let suma = 0;
  for (let i = 0; i < alturas.length; i++) {
    suma += alturas[i];
    if (suma > disponible) {
      return i;
    }
  }
  return alturas.length;
}

/** La clase con que la lista deja cada renglón a su alto natural mientras se mide (tablero-cc.component.css). */
const CLASE_MIDIENDO = "is-midiendo";
/** Lo más que crece cada renglón sobre su alto natural (px) para que la lista llegue al pie. */
const HOLGURA_MAXIMA = 40;

/** Lo que se midió de una lista de renglones (un CSS grid de una columna, como la de proyectos del tablero). */
export interface MedidaLista {
  /** El alto natural de cada renglón, de la primera página a la última (px). */
  altos: number[];
  /** Lo que queda para los renglones hasta el final de la ventana (px). */
  disponible: number;
  /** El espacio entre renglones (row-gap, px). */
  separacion: number;
  /** El relleno de arriba más el de abajo de la lista (px). */
  relleno: number;
}

/** Cómo se pinta una página: las pistas de sus renglones y el alto de la lista (px). */
export interface AltoLista {
  /** El grid-template-rows de la página ("minmax(auto, 104px) minmax(auto, 122px) ..."). */
  filas: string;
  /**
   * El alto de la lista, relleno incluido: llega al final de la ventana y es el mismo en cada página (el paginado no
   * brinca). 0 = sin alto fijo: una sola página que no llena la ventana termina en su último renglón.
   */
  alto: number;
}

/**
 * Deja la lista lista para medir: la topa a su alto de ahora, con lo que
 * sobresale recortado (pintar los renglones de las demás páginas no estira la
 * página ni cambia su barra de desplazamiento, y con ella el ancho), y la pasa
 * a `is-midiendo`: cada renglón a su alto natural. Devuelve con qué deshacerlo.
 */
export function preparaMedida(lista: HTMLElement): () => void {
  lista.style.maxHeight = `${lista.getBoundingClientRect().height}px`;
  lista.classList.add(CLASE_MIDIENDO);
  return () => {
    lista.classList.remove(CLASE_MIDIENDO);
    lista.style.removeProperty("max-height");
  };
}

/**
 * Mide `lista` (ya preparada y con los renglones de cada página pintados): el
 * alto natural de cada renglón y lo que queda hasta el final de la ventana,
 * descontando el pie y el borde de la tarjeta contenedora y `margen`. Como en
 * ajustePaginado, cuentan los altos REALES (un nombre o unos datos en dos
 * renglones piden más).
 */
export function mideLista(lista: HTMLElement, margen: number): MedidaLista {
  const estilo = getComputedStyle(lista);
  const separacion = Number.parseFloat(estilo.rowGap) || 0;
  const relleno = (Number.parseFloat(estilo.paddingTop) || 0) + (Number.parseFloat(estilo.paddingBottom) || 0);
  const altos = acotadas(Array.from(lista.children, (r) => Math.ceil(r.getBoundingClientRect().height)));
  const arriba = lista.getBoundingClientRect().top;
  const contenedora = lista.closest(".table-page__card");
  const pie = contenedora?.querySelector(".table-page__footer")?.getBoundingClientRect().height ?? ALTO_PIE_OMISION;
  const borde = contenedora ? Number.parseFloat(getComputedStyle(contenedora).borderBottomWidth) || 0 : 0;
  const disponible = Math.max(0, Math.floor(window.innerHeight - arriba - relleno - pie - borde - margen));
  return { altos, disponible, separacion, relleno };
}

/**
 * Los renglones por página: los más que quepan en CADA página (ningún renglón
 * de ninguna página pasa del final de la ventana), a lo más `maximo`; uno si
 * ni uno cabe (una ventana muy baja).
 */
export function porPaginaLista(m: MedidaLista, maximo: number): number {
  for (let k = Math.min(maximo, m.altos.length); k > 1; k--) {
    if (cabenLasPaginas(m, k)) {
      return k;
    }
  }
  return 1;
}

function cabenLasPaginas(m: MedidaLista, porPagina: number): boolean {
  for (let i = 0; i < m.altos.length; i += porPagina) {
    if (ocupado(m, m.altos.slice(i, i + porPagina)) > m.disponible) {
      return false;
    }
  }
  return true;
}

function ocupado(m: MedidaLista, altos: number[]): number {
  return altos.reduce((s, h) => s + h, 0) + Math.max(0, altos.length - 1) * m.separacion;
}

/**
 * Las pistas de la página `pagina`: cada renglón crece lo mismo, lo que sobra
 * repartido, hasta que la lista llega al final de la ventana (sin hueco ni
 * scroll), con tope, y nunca queda por debajo de su contenido (minmax(auto, …)).
 * La última página de varias, si trae menos renglones, los pinta con lo que
 * crecen en la más apretada de las llenas (se ven igual) y deja libre el resto:
 * la lista mide lo mismo en cada página y el paginado no se mueve bajo el ratón.
 * Una sola página (un filtro que deja pocos) que no alcanza a llenar la ventana
 * ni con el tope va a su alto natural y sin alto fijo: la tarjeta termina en su
 * último renglón, sin hueco dentro de la lista.
 */
export function altoLista(m: MedidaLista, porPagina: number, pagina: number): AltoLista {
  const holgura = (altos: number[]) => Math.floor((m.disponible - ocupado(m, altos)) / Math.max(1, altos.length));
  const llenas: number[] = [];
  for (let i = 0; i + porPagina <= m.altos.length; i += porPagina) {
    llenas.push(holgura(m.altos.slice(i, i + porPagina)));
  }
  const pintados = m.altos.slice((pagina - 1) * porPagina, pagina * porPagina);
  const unaPagina = m.altos.length <= porPagina;
  if (unaPagina && holgura(pintados) > HOLGURA_MAXIMA) {
    return { filas: pintados.map((h) => `minmax(auto, ${h}px)`).join(" "), alto: 0 };
  }
  const crece = pintados.length < porPagina && !unaPagina ? Math.min(...llenas) : holgura(pintados);
  const extra = Math.min(HOLGURA_MAXIMA, Math.max(0, crece));
  // Los píxeles que deja el redondeo (menos que renglones) van uno a uno a los primeros: el último llega justo al pie.
  const lleno = (pintados.length === porPagina || unaPagina) && crece >= 0 && crece <= HOLGURA_MAXIMA;
  const resto = lleno ? m.disponible - ocupado(m, pintados) - extra * pintados.length : 0;
  return {
    filas: pintados.map((h, i) => `minmax(auto, ${h + extra + (i < resto ? 1 : 0)}px)`).join(" "),
    alto: Math.round(m.disponible + m.relleno),
  };
}

/**
 * Si `raiz` o alguna de sus cajas que cumple `selector` tiene más contenido
 * del que cabe a lo ancho: lo que no se parte en renglones y se saldría de su
 * columna, o columnas que ya no caben en la lista.
 */
export function desborda(raiz: HTMLElement, selector: string): boolean {
  return [raiz, ...Array.from(raiz.querySelectorAll(selector))].some((c) => c.scrollWidth > c.clientWidth + 1);
}

/**
 * Si la cuenta nueva es mayor que la vigente pero la pasada anterior la
 * ENCOGIÓ, se queda la vigente: así nunca brinca entre dos valores cuando la
 * fila que sobra es más alta que el promedio. Devuelve la cuenta que se aplica.
 */
export function cuentaEstable(vigente: number, propuesta: number, anteriorEncogio: boolean): number {
  if (propuesta > vigente && anteriorEncogio) {
    return vigente;
  }
  return propuesta;
}

/** Los números de página, 1..n. */
export function numerosDePagina(total: number, porPagina: number): number[] {
  const paginas = Math.max(1, Math.ceil(total / Math.max(1, porPagina)));
  return Array.from({ length: paginas }, (_, i) => i + 1);
}

/** "Mostrando 1-8 de 12 proyectos" o "12 proyectos" cuando cabe en una página. */
export function textoRango(total: number, pagina: number, porPagina: number, singular: string, plural: string): string {
  const totalTexto = new Intl.NumberFormat("es-MX").format(total);
  if (total <= porPagina) {
    return `${totalTexto} ${total === 1 ? singular : plural}`;
  }
  const inicio = (pagina - 1) * porPagina + 1;
  return `Mostrando ${inicio}-${Math.min(inicio + porPagina - 1, total)} de ${totalTexto} ${plural}`;
}
